// Per-loadout weapon stats the client needs for view logic (recoil recovery, zoom, FX).
// The numbers come from `weaponStatsJson` (gr_core::weapons::resolve_stats, serde camelCase).

export interface ClientWeaponStats {
  id: string;
  fireInterval: number;
  pellets: number;
  adsFovMult: number;
  scopeOverlay: boolean;
  hideFlash: boolean;
  /** Degrees per second at which the uncompensated kick returns. */
  recoveryRate: number;
  /** Seconds after the newest shot before the uncompensated kick starts to return. */
  recoveryDelay: number;
  /** Seconds without a shot after which the spray index resets. */
  sprayReset: number;
  /** Meters at which a shot is heard. */
  soundRange: number;
}

export type StatsJsonFn = (weaponId: string, attachments: string[], perks: string[]) => string;

const FALLBACK: Omit<ClientWeaponStats, "id"> = {
  fireInterval: 0.1,
  pellets: 1,
  adsFovMult: 0.8,
  scopeOverlay: false,
  hideFlash: false,
  recoveryRate: 10,
  recoveryDelay: 0.1,
  sprayReset: 0.3,
  soundRange: 120,
};

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

export function parseWeaponStats(id: string, json: string): ClientWeaponStats {
  let raw: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(json);
    if (parsed && typeof parsed === "object") raw = parsed as Record<string, unknown>;
  } catch {
    // Keep the fallbacks: a stats failure must not break the frame loop.
  }
  const recoil = (raw["recoil"] ?? {}) as Record<string, unknown>;
  return {
    id,
    fireInterval: num(raw["fireInterval"], FALLBACK.fireInterval),
    pellets: num(raw["pellets"], FALLBACK.pellets),
    adsFovMult: num(raw["adsFovMult"], FALLBACK.adsFovMult),
    scopeOverlay: raw["scopeOverlay"] === true,
    hideFlash: raw["hideFlash"] === true,
    recoveryRate: num(recoil["recoveryRate"], FALLBACK.recoveryRate),
    recoveryDelay: num(recoil["recoveryDelay"], FALLBACK.recoveryDelay),
    sprayReset: num(recoil["sprayReset"], FALLBACK.sprayReset),
    soundRange: num(raw["soundRange"], FALLBACK.soundRange),
  };
}

const NO_ATTACHMENTS: string[] = [];

/** Weapon stats of the current life, resolved lazily per weapon id. */
export class WeaponStatsCache {
  private readonly cache = new Map<string, ClientWeaponStats>();
  private readonly attachments = new Map<string, string[]>();
  private readonly camos = new Map<string, string>();
  private perks: string[] = [];

  constructor(private readonly statsJson: StatsJsonFn) {}

  /** Declares the weapons of the new loadout and drops everything resolved before. */
  setLoadout(weapons: readonly { id: string; attachments: readonly string[]; camo: string }[], perks: readonly string[]): void {
    this.cache.clear();
    this.attachments.clear();
    this.camos.clear();
    for (const w of weapons) {
      this.attachments.set(w.id, [...w.attachments]);
      this.camos.set(w.id, w.camo || "none");
    }
    this.perks = [...perks];
  }

  /** Attachments of a loadout weapon (stable array identity, empty for unknown ids). */
  attachmentsOf(weaponId: string): string[] {
    return this.attachments.get(weaponId) ?? NO_ATTACHMENTS;
  }

  camoOf(weaponId: string): string {
    return this.camos.get(weaponId) ?? "none";
  }

  get(weaponId: string): ClientWeaponStats {
    let s = this.cache.get(weaponId);
    if (!s) {
      let json = "{}";
      try {
        json = this.statsJson(weaponId, this.attachments.get(weaponId) ?? [], this.perks);
      } catch {
        // Unknown weapon id: fallbacks.
      }
      s = parseWeaponStats(weaponId, json);
      this.cache.set(weaponId, s);
    }
    return s;
  }
}

/** Stats of a remote player's weapon (loadout attachments are not public: base weapon, no perks). */
export class RemoteStatsCache {
  private readonly cache = new Map<string, ClientWeaponStats>();

  constructor(private readonly statsJson: StatsJsonFn) {}

  get(weaponId: string, attachments: string[]): ClientWeaponStats {
    const key = attachments.length === 0 ? weaponId : `${weaponId}+${attachments.join(",")}`;
    let s = this.cache.get(key);
    if (!s) {
      let json = "{}";
      try {
        json = this.statsJson(weaponId, attachments, []);
      } catch {
        // Unknown weapon id: fallbacks.
      }
      s = parseWeaponStats(weaponId, json);
      this.cache.set(key, s);
    }
    return s;
  }
}
